/* eslint-disable no-shadow */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

import { EventProfileTabIdentifiers } from '../../../../../utils/constants';
import { eventStateTypeIds, regStepIds, sportTypeIds } from '../../../../../utils/eventConstants';
import USAPLogo from '../../../../../components/USAPLogo';
import { hasAdminAddPaymentRequiredInvitation, hasPlayerInvitation, hasPlayerInvitationAndRegDateOpen, isDivisionCompletelyRegistered, isDoublesDivision, isPickleBallSportTypeEvent, showCompleteRegistrationButton, isEventType, EventTypes } from '../../../../../utils/events';
import { isoCountryCode2 } from '../../../../../utils/isoCountryCodes';
import { resetSelectedDivisionsBasedOnDate } from '../../../../../actions/event-profile-actions';
import RegEditDropdown from './RegEditDropdown';
import { useStatsig } from '../../../../../components/hooks/useStatsig';
import { routerHistoryShape } from '../../../../../propTypes';
import Img from '../../../../../components/Img';

function RegistrationCtaV3(props) {
    const { isCurrentMemberAdmin, handleRegClick, handleTabClick, sportTypeId, previewOnly, isRegistrationIncomplete, hasPendingAdminInvitation } = props;
    const auth = useSelector(({ auth }) => auth);
    const dispatch = useDispatch();
    const { event, registrations } = useSelector(({ eventProfile }) => {
        const { event, registrations } = eventProfile;
        return { event, registrations };
    });
    const [canSwitchDivisions, setCanSwitchDivisions] = useState();
    const [isUserEligibleForRegistration, setIsUserEligibleForRegistration] = useState();
    const isAddChangePartnerAllowed = event.data?.changePartnerAllowed;
    const { checkGate, featureGates } = useStatsig();
    const isReferFriendsEnabled = checkGate(featureGates.FLEX_REFERRAL);
    const isStreamingLinkEnabled = checkGate(featureGates.IOS_WEB_STREAMING_LINK);

    useEffect(() => {
        const canRegister = !!registrations?.data?.availableDivisions?.length;
        const canSwitch = registrations?.data?.registeredDivisions?.some(div => div.availableDivisionsForSwitching.length);

        if (isUserEligibleForRegistration !== canRegister) {
            setIsUserEligibleForRegistration(canRegister);
        }
        if (canSwitchDivisions !== canSwitch) {
            setCanSwitchDivisions(canSwitch);
        }
    }, [registrations?.data]);

    const nonEntryFeeDivisions = event.data?.eventDivisions.filter(division => division.isEventEntryFeeDivision === false);
    const hasPlayersRegistered = nonEntryFeeDivisions?.some(division => division.registeredCount > 0);
    const isUserPlaying = event.data?.currentMember?.isPlaying;
    const hasPreRegInvite = event?.data?.currentMember?.memberPreRegistrationInvites?.length > 0;
    const hasPartnerInvite = event?.data?.currentMember?.registeredDivisions?.some(div => isDoublesDivision(div) && hasPlayerInvitation(div));
    const hasAdminAddPaymentRequiredInvite = event?.data?.currentMember?.registeredDivisions?.some(div => hasAdminAddPaymentRequiredInvitation(div));
    const usapVerificationTypeId = event?.data?.usapVerificationTypeId;
    const usapApplicationStatusId = event?.data?.usapApplicationStatusId;
    const isPickleBallEvent = isPickleBallSportTypeEvent(sportTypeId);
    const isNotCompleted = event.data.eventStateId !== eventStateTypeIds.COMPLETED;
    const isInProgressOrCompleted = event.data.eventStateId === eventStateTypeIds.IN_PROGRESS || event.data.eventStateId === eventStateTypeIds.COMPLETED;
    const isEventCompleted = event.data.eventStateId === eventStateTypeIds.COMPLETED;
    const registrationClosed = 'Registration Closed';
    const editRegistration = 'Add Division';
    const completeRegistration = 'Complete Registration';
    const addDivisions = 'Add Division';
    const isRegistrationInPreReg = event.data.registrationStateId === eventStateTypeIds.PRE_REGISTRATION;
    const isOpenRegistration = event.data.registrationStateId === eventStateTypeIds.OPEN_REGISTRATION;
    const isRegistrationClosed = event.data.registrationStateId === eventStateTypeIds.REGISTRATION_CLOSED;
    const isRegistrationValid = isOpenRegistration && isNotCompleted;
    const isUserRegisteredToAllDivisions = nonEntryFeeDivisions?.every(division => isDivisionCompletelyRegistered(division, event?.data));
    const isUSEvent = event.data.eventLocations?.length > 0 && event.data.eventLocations[0].countryCode2 === isoCountryCode2.USA;
    const isFlexLeagueEvent = isEventType(event.data, EventTypes.FLEX_LEAGUE);
    const showCompleteRegistrationBtn = (showCompleteRegistrationButton(event.data?.currentMember?.registeredDivisions || [], event.data?.eventDivisions) || isRegistrationIncomplete || hasPendingAdminInvitation) && !isRegistrationClosed;
    const hasSelfWithDrawAllowed = event.data?.eventDivisions?.some(division => division.selfWithdrawAllowed === true);
    const showEditRegistration = (isUserPlaying && isOpenRegistration && hasSelfWithDrawAllowed) || showEditRegistrationButton(event?.data?.currentMember?.registeredDivisions, nonEntryFeeDivisions);
    const showAddDivisions = (isUserPlaying && isOpenRegistration) ||
        (isUserPlaying && isOpenRegistration && !hasSelfWithDrawAllowed);
    const hasAllDivsSelfWithDrawDisabled = event.data?.eventDivisions?.every(division => division.selfWithdrawAllowed === false);
    const showSelfWithdrawButton = registrations?.data?.registeredDivisions?.some(div => div.selfWithdrawAllowed) && !isRegistrationIncomplete;
    const showReferFriendsButton = isOpenRegistration && isFlexLeagueEvent && isReferFriendsEnabled && isUSEvent;

    // eligible list related variables
    const isEligibleListEnabled = event?.data?.preRegAndEligibleListEnabled;
    const isUserEligibleForEvent = event?.data?.currentMember?.isEligibleForEvent;
    const hasExcludedDivisions = event?.data?.eventDivisions?.some(division => division.excludeFromEligibleListRestrictions);

    // Determines whether the Register button should be shown based on event state and eligibility
    const shouldShowRegisterButton = () => {
        // Handle PRE-REGISTRATION state
        if (isRegistrationInPreReg) {
            if (isUserPlaying) {
                return true;
            }
            return hasPreRegInvite || hasPartnerInvite || hasAdminAddPaymentRequiredInvite;
        }

        // Handle OPEN_REGISTRATION + Eligible Players feature
        if (isOpenRegistration && isEligibleListEnabled) {
            return isUserEligibleForEvent || hasExcludedDivisions;
        }

        // Default to true if none of the restrictions apply
        return true;
    };

    const showEligibleListInfo = () => {
        if (isOpenRegistration && isEligibleListEnabled) {
            return !(isUserEligibleForEvent || hasExcludedDivisions);
        }
        return false;
    };

    const showCompleteRegistrationLabelForSingleDivisionEvent = () => {
        if (nonEntryFeeDivisions.length === 1) {
            let isPlayerInvited = false;
            (event.data?.currentMember?.registeredDivisions || []).forEach((invitedDivision) => {
                const division = nonEntryFeeDivisions.find((div) => {
                    return div.id === invitedDivision.id;
                });
                if (isDoublesDivision(division)) {
                    isPlayerInvited = hasPlayerInvitationAndRegDateOpen(invitedDivision, division);
                }
            });
            return isPlayerInvited;
        }
    };
    function showEditRegistrationButton(userRegisteredDivisions, nonentrydivisions) {
        const matchingUserDivisions = userRegisteredDivisions?.filter(division => nonentrydivisions?.some(d => d.id === division.id));
        // Check if any matching division allows self-cancellation
        const hasSelfCancelDivision = matchingUserDivisions?.some(division => {
            const matchingDivision = nonentrydivisions?.find(d => d.id === division.id);
            return matchingDivision?.selfWithdrawAllowed;
        });
        // Check if deadline hasn't passed for all matching registered divisions
        return hasSelfCancelDivision && matchingUserDivisions?.every(division => {
            const matchingDivision = nonentrydivisions?.find(d => d.id === division.id);
            return matchingDivision && new Date(matchingDivision.maxSelfWithdrawDateUtc) > new Date();
        });
    }
    let regButtonLabel = 'Register';
    if (showAddDivisions && !showCompleteRegistrationLabelForSingleDivisionEvent() && !showCompleteRegistrationBtn) {
        regButtonLabel = addDivisions;
    } else if (showCompleteRegistrationBtn || showCompleteRegistrationLabelForSingleDivisionEvent()) {
        regButtonLabel = completeRegistration;
    } else if (showEditRegistration) {
        regButtonLabel = editRegistration;
    }
    const hideButtons = (!shouldShowRegisterButton()) ||
        (isOpenRegistration && isUserPlaying && nonEntryFeeDivisions?.length === 1 && hasPlayersRegistered && !showCompleteRegistrationLabelForSingleDivisionEvent() && !showEditRegistrationButton(event?.data?.currentMember?.registeredDivisions, nonEntryFeeDivisions)) ||
        (isUserRegisteredToAllDivisions && !showEditRegistrationButton(event?.data?.currentMember?.registeredDivisions, nonEntryFeeDivisions) && isRegistrationClosed) ||
        (isUserRegisteredToAllDivisions && hasAllDivsSelfWithDrawDisabled && isOpenRegistration && !isAddChangePartnerAllowed);

    const handleRegButtonClick = () => {
        if (isUserPlaying && showSelfWithdrawButton) {
            handleRegClick(null, null, null, regStepIds.DIVISIONS_ADD);
        } else {
            handleRegClick();
        }
    };
    const canByPassRegistrationClosedForAdminInvitation = isRegistrationClosed && !isEventCompleted && hasPendingAdminInvitation;
    const showSwitchButton = isUserEligibleForRegistration && canSwitchDivisions;

    const dropdownActions = {
        REGISTER: 'register',
        SWITCH: 'switch',
        WITHDRAW: 'withdraw',
        CHANGE_PARTNER: 'changePartner',
    };

    const showRegButton = isOpenRegistration || (isRegistrationInPreReg && (hasPartnerInvite || hasPreRegInvite));

    // edit registration dropdown for registered users
    const dropdownOptions = [];

    if (showRegButton) {
        dropdownOptions.push({ id: dropdownActions.REGISTER, label: regButtonLabel });
    }
    if (canSwitchDivisions) {
        dropdownOptions.push({ id: dropdownActions.SWITCH, label: 'Switch Division' });
    }
    if (showSelfWithdrawButton) {
        dropdownOptions.push({ id: dropdownActions.WITHDRAW, label: 'Withdraw' });
    }
    if (isAddChangePartnerAllowed) {
        dropdownOptions.push({ id: dropdownActions.CHANGE_PARTNER, label: 'Add/Change Partner' });
    }

    const onEditRegDropdownClick = (option) => {
        switch (option) {
            case dropdownActions.REGISTER:
                handleRegButtonClick();
                dispatch(resetSelectedDivisionsBasedOnDate());
                break;
            case dropdownActions.SWITCH:
                handleRegClick(null, null, null, regStepIds.DIVISIONS_SWITCH);
                dispatch(resetSelectedDivisionsBasedOnDate());
                break;
            case dropdownActions.WITHDRAW:
                handleRegClick(null, null, null, regStepIds.DIVISIONS_CANCEL);
                dispatch(resetSelectedDivisionsBasedOnDate());
                break;
            case dropdownActions.CHANGE_PARTNER:
                handleRegClick(null, null, null, regStepIds.CHANGE_PARTNER);
                dispatch(resetSelectedDivisionsBasedOnDate());
                break;
            default:
                break;
        }
    };

    const hasStreamingLink = isStreamingLinkEnabled && !!event.data?.streamingLink;
    const streamingLink = event.data?.streamingLink;
    let streamingLinkHref = streamingLink;
    if (streamingLink && !/^https?:\/\//i.test(streamingLink)) {
        streamingLinkHref = `https://${streamingLink}`;
    }

    return (
        <div className="register mb16">
            <div>
                <div className="pt8 d-flex align-items-center flex-wrap">
                    {((event.data.eventStateId === eventStateTypeIds.PRE_REGISTRATION || event.data.eventStateId === eventStateTypeIds.REGISTRATION_CLOSED) && !(isUserPlaying || hasPreRegInvite)) && (
                        <>
                            {!isRegistrationClosed && (
                                <button
                                    type="button"
                                    className="btn btn-primary btn-lg ml8 mr8 mb8 text-uppercase disabled "
                                    disabled={true}
                                    data-testid="event-profile.registration-cta.registration-unavailable-btn"
                                >
                                    {event.data.eventStateId === eventStateTypeIds.PRE_REGISTRATION ? `Registration begins on ${event.data.eventSchedule.registrationStartShort}` : 'Registration is closed'}
                                </button>
                            )}
                        </>
                    )}
                    {(isRegistrationValid || isUserPlaying || hasPreRegInvite || canByPassRegistrationClosedForAdminInvitation) && (
                        <>
                            {!isRegistrationClosed && !hideButtons && (
                                <>
                                    {auth.user && (isUserPlaying || hasPreRegInvite) && event.data.published ? (
                                        <>
                                            {dropdownOptions.length > 1 ? (
                                                <RegEditDropdown
                                                    options={dropdownOptions}
                                                    onOptionClick={onEditRegDropdownClick}
                                                />
                                            ) : (
                                                <>
                                                    {showRegButton && (
                                                        <button
                                                            type="button"
                                                            className={`btn btn-primary ml8 mr8 btn-lg text-uppercase mb8`}
                                                            onClick={() => {
                                                                handleRegButtonClick();
                                                                dispatch(resetSelectedDivisionsBasedOnDate());
                                                            }}
                                                            data-testid="event-profile.registration-cta.reg-btn"
                                                        >
                                                            {regButtonLabel}
                                                        </button>
                                                    )}
                                                    {showSwitchButton && (
                                                        <button
                                                            type="button"
                                                            className={`btn btn-primary ml8 mr8 btn-lg text-uppercase mb8`}
                                                            onClick={() => {
                                                                handleRegClick(null, null, null, regStepIds.DIVISIONS_SWITCH);
                                                                dispatch(resetSelectedDivisionsBasedOnDate());
                                                            }}
                                                            data-testid="event-profile.registration-cta.switch-division-btn"
                                                        >
                                                            Switch Division
                                                        </button>
                                                    )}
                                                    {showSelfWithdrawButton && (
                                                        <button
                                                            type="button"
                                                            className={`btn btn-primary ml8 mr8 btn-lg text-uppercase mb8`}
                                                            onClick={() => {
                                                                handleRegClick(null, null, null, regStepIds.DIVISIONS_CANCEL);
                                                                dispatch(resetSelectedDivisionsBasedOnDate());
                                                            }}
                                                            data-testid="event-profile.registration-cta.withdraw-btn"
                                                        >
                                                            Withdraw
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {(!isRegistrationInPreReg || canByPassRegistrationClosedForAdminInvitation) && (
                                                <button
                                                    type="button"
                                                    className={`btn btn-primary ml8 mr8 btn-lg text-uppercase ${event.data.published ? '' : 'disabled'} mb8`}
                                                    disabled={!event.data.published}
                                                    onClick={() => {
                                                        handleRegButtonClick();
                                                        dispatch(resetSelectedDivisionsBasedOnDate());
                                                    }}
                                                    data-testid="event-profile.registration-cta.reg-btn"
                                                >
                                                        {auth.user && isUserPlaying ? regButtonLabel : 'Register'}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                    {(isInProgressOrCompleted) && (
                        <button
                            type="button"
                            className="btn btn-primary ml8 mr8 btn-lg text-uppercase mb8"
                            onClick={() => handleTabClick({ tabId: EventProfileTabIdentifiers.DRAWS })}
                            data-testid="event-profile.registration-cta.view-draws-btn"
                        >
                            {event.data.eventStateId === eventStateTypeIds.IN_PROGRESS ? 'In Progress' : 'Completed'} - View {isFlexLeagueEvent ? 'Matchups' : 'Draws'}
                        </button>
                    )}
                    {!previewOnly && (
                        <button
                            type="button"
                            className="ml8 mr8 cursor-pointer btn btn-share btn-base btn-lg text-uppercase mb8"
                            onClick={() => {
                                props.showShareUrlPopup();
                            }}
                            data-testid="event-profile.registration-cta.share-btn"
                        >
                            Share
                        </button>
                    )}
                    {hasStreamingLink && (
                        <a
                            href={streamingLinkHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-watch cursor-pointer ml8 mr8 btn-lg text-uppercase mb8"
                            data-testid="event-profile.registration-cta.watch-btn"
                        >
                            {isEventCompleted ? 'Replays' : 'Watch'}
                            <Img className="ml8" width="10" height="10" src={'/img/v2/icons/icon-play-white.svg'} alt="" data-testid="event-profile.registration-cta.watch-btn.play-icon" />
                        </a>
                    )}
                    {showReferFriendsButton && (
                        <button
                            type="button"
                            className={`ml8 mr8 mb8 cursor-pointer btn btn-base btn-lg text-uppercase`}
                            onClick={() => {
                                props.history.push('/flex-league-referral');
                            }}
                            data-testid="event-profile.registration-cta.refer-friends-btn"
                        >
                            Refer Friends
                        </button>
                    )}
                </div>
                <div className="d-inline-block" data-testid="event-profile.registration-cta.usap-logo">
                    {isPickleBallEvent && <USAPLogo usapApplicationStatusId={usapApplicationStatusId} usapVerificationTypeId={usapVerificationTypeId} />}
                </div>
                {!isEventCompleted && (event.data.eventSchedule.registrationEndString !== registrationClosed || showEligibleListInfo()) && (
                    <p className="text-small mt8" data-testid="event-profile.registration-deadline">{showEligibleListInfo() ? 'Only players on the eligible list can register to this event.' : event.data.eventSchedule.registrationEndString}</p>
                )}
            </div>
            {isCurrentMemberAdmin && event.data.eventStateId !== eventStateTypeIds.COMPLETED && event.data.registrationStateId === eventStateTypeIds.REGISTRATION_CLOSED && !isUserPlaying && (
                <p className="text-small mt8" data-testid="event-profile.registration-cta.admin-reopen-msg">
                    Need to add a player after registration has closed? <Link to={`/events/${event.data.id}/edit?step=1/`}>Click here</Link> to reopen registration
                </p>
            )}
        </div>
    );
}

RegistrationCtaV3.propTypes = {
    isCurrentMemberAdmin: PropTypes.bool,
    handleRegClick: PropTypes.func,
    handleTabClick: PropTypes.func,
    sportTypeId: PropTypes.number,
    previewOnly: PropTypes.bool,
    showShareUrlPopup: PropTypes.func,
    history: routerHistoryShape.isRequired,
    isRegistrationIncomplete: PropTypes.bool,
    hasPendingAdminInvitation: PropTypes.bool,
};
RegistrationCtaV3.defaultProps = {
    isCurrentMemberAdmin: false,
    handleRegClick: () => { },
    handleTabClick: () => { },
    showShareUrlPopup: () => { },
    sportTypeId: sportTypeIds.TENNIS,
    previewOnly: false,
    isRegistrationIncomplete: false,
    hasPendingAdminInvitation: false,
};

export default withRouter(RegistrationCtaV3);
